import { FiX } from "react-icons/fi";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../store/hooks";
import { casesPortalCaseHref } from "../../utils/linkedPortalCase";

interface SmileCaseDetailsModalProps {
    show: boolean;
    onClose: () => void;
    isFetchingCase: boolean;
    selectedCase: any;
    accessToken: string;
}

const SmileCaseDetailsModal = ({
    show,
    onClose,
    isFetchingCase,
    selectedCase,
    accessToken,
}: SmileCaseDetailsModalProps) => {
    const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
    const role = useAppSelector((state) => state.auth.user?.role);
    const linkedCaseId = selectedCase?.caseDetails?.case_id as string | undefined;

    useEffect(() => {
        const loadThumbnails = async () => {
            if (!selectedCase?.caseFiles?.length || !accessToken) return;

            const thumbnailMap: Record<string, string> = {};

            await Promise.all(
                selectedCase.caseFiles.map(async (file: any) => {
                    try {
                        const response = await fetch(
                            `https://api.box.com/2.0/files/${file.file_id}/thumbnail.jpg?min_height=400&min_width=400`,
                            {
                                headers: {
                                    Authorization: `Bearer ${accessToken}`,
                                },
                            }
                        );

                        if (!response.ok) return;

                        const blob = await response.blob();

                        thumbnailMap[file.file_id] =
                            URL.createObjectURL(blob);
                    } catch (error) {
                        console.error(
                            `Failed to load thumbnail for ${file.file_id}`,
                            error
                        );
                    }
                })
            );

            setThumbnails(thumbnailMap);
        };

        if (show) {
            loadThumbnails();
        }
    }, [selectedCase, accessToken, show]);

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
            <div className="smile-dark-scroll w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-[#101211] border border-[#1f2221] rounded-2xl sm:rounded-3xl">
                <div className="sticky top-0 z-10 flex items-center justify-between p-4 sm:p-6 border-b border-[#1f2221] bg-[#101211]">
                    <h2 className="text-xl sm:text-2xl font-bold text-cyan-400">
                        Case Details
                    </h2>

                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white"
                    >
                        <FiX size={24} />
                    </button>
                </div>

                <div className="p-4 sm:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                        <div className="bg-[#141716] p-4 rounded-xl">
                            <p className="text-sm text-[#8b8f8d]">
                                Smile ID
                            </p>
                            <p className="mt-1 font-medium text-white">
                                {selectedCase?.caseDetails?.smile_id}
                            </p>
                        </div>

                        <div className="bg-[#141716] p-4 rounded-xl">
                            <p className="text-sm text-[#8b8f8d]">
                                Cases portal
                            </p>
                            {linkedCaseId ? (
                                <a
                                    href={casesPortalCaseHref(linkedCaseId, role)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-1 inline-block font-medium text-[#2fa9bf] hover:underline break-all"
                                >
                                    {linkedCaseId}
                                </a>
                            ) : (
                                <p className="mt-1 font-medium text-[#8b8f8d]">—</p>
                            )}
                        </div>

                        <div className="bg-[#141716] p-4 rounded-xl">
                            <p className="text-sm text-[#8b8f8d]">
                                Patient Name
                            </p>
                            <p className="mt-1 font-medium text-white">
                                {selectedCase?.caseDetails?.patient_name}
                            </p>
                        </div>
                    </div>

                    <h3 className="text-xl font-semibold mb-4 text-cyan-400">
                        Files
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {selectedCase?.caseFiles?.map((file: any) => (
                            <div
                                key={file.id}
                                className="bg-[#141716] border border-[#1f2221] rounded-2xl overflow-hidden"
                            >
                                <div className="aspect-square bg-[#0d0f0e]">
                                    {thumbnails[file.file_id] ? (
                                        <img
                                            src={thumbnails[file.file_id]}
                                            alt={file.file_name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-[#8b8f8d]">
                                            Loading...
                                        </div>
                                    )}
                                </div>

                                <div className="p-4">
                                    <p
                                        className="font-medium text-white truncate"
                                        title={file.file_name}
                                    >
                                        {file.file_name}
                                    </p>

                                    <span className="inline-flex mt-3 px-3 py-1 text-xs rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                                        {file.file_type}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SmileCaseDetailsModal;